import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/shared/user.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-sso-callback',
  template: `
    <div class="sso-callback-container">
      <div class="spinner-container" *ngIf="loading">
        <mat-spinner diameter="50"></mat-spinner>
        <p class="mt-3">Validating authentication...</p>
      </div>
      <div class="error-container" *ngIf="error && !loading">
        <h2>Authentication Error</h2>
        <p>{{ error }}</p>
        <button mat-raised-button color="primary" (click)="retryLogin()" class="mt-3">
          Retry Login
        </button>
      </div>
    </div>
  `,
  styles: [`
    .sso-callback-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      flex-direction: column;
      background: #f5f5f5;
    }
    .spinner-container {
      text-align: center;
    }
    .error-container {
      text-align: center;
      padding: 40px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      max-width: 500px;
    }
    .error-container h2 {
      color: #d32f2f;
      margin-bottom: 20px;
    }
    .error-container p {
      color: #666;
      margin-bottom: 20px;
    }
  `]
})
export class SSOCallbackComponent implements OnInit {
  loading = true;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private toast: ToastrService
  ) {}

  ngOnInit(): void {
    // Extract token and username from query parameters
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      const username = params['username'];

      if (token && username) {
        this.validateToken(token, username);
      } else {
        // No token in URL - check for error parameters
        const error = params['error'];
        const errorDescription = params['error_description'];
        
        if (error) {
          // OAuth2 error - redirect to error page
          this.router.navigate(['/error'], {
            queryParams: {
              type: 'oauth2_callback',
              message: errorDescription || 'OAuth2 authentication failed',
              details: `Error code: ${error}`
            },
            replaceUrl: true
          });
        } else {
          // No token and no error - redirect to SSO login
          this.error = 'No authentication token received. Redirecting to login...';
          setTimeout(() => {
            this.initiateSSOLogin();
          }, 2000);
        }
      }
    });
  }

  validateToken(token: string, username: string): void {
    this.userService.validateSSOToken(token, username)
      .then(() => {
        this.loading = false;
        this.toast.success('Login successful, Redirecting!', '');
        // Clear URL parameters
        this.router.navigate(['/app/dashboard'], { replaceUrl: true });
      })
      .catch((error) => {
        this.loading = false;
        const errorMessage = error?.error?.message || error?.message || 'Token validation failed. Please try again.';
        this.error = errorMessage;
        console.error('Token validation failed:', error);
        
        // Redirect to error page for better error handling
        setTimeout(() => {
          this.router.navigate(['/error'], {
            queryParams: {
              type: 'token_validation',
              message: errorMessage,
              details: error?.error?.details || ''
            },
            replaceUrl: true
          });
        }, 3000); // Show error message for 3 seconds before redirecting
      });
  }

  initiateSSOLogin(): void {
    this.userService.initiateSSOLogin();
  }

  retryLogin(): void {
    this.error = null;
    this.loading = true;
    this.initiateSSOLogin();
  }
}

